import {
  type Descendant,
  Editor,
  type EditorAboveOptions,
  Element,
  type ElementEntry,
  type Location,
  Node,
  type NodeEntry,
  Path,
  type PathRef,
  Point,
  Range,
  Transforms,
  Text,
} from 'slate';
import { type ListElement, type ListItemElement } from '../../types/aha-editor';
import AhaEditor from '../../AhaEditor';
import removeTree from '../../util/removeTree';
import PathUtils from '../../AhaEditorUtils/PathUtils';
import { isList, isListItem, isListItemEntry, SUPPORTED_TYPES } from './guards';
import NodeUtils from '../../AhaEditorUtils/NodeUtils';

export const getSelectedListItems = (editor: Editor) => {
  // The editor's selection will always be between two leaf nodes. Because of this
  // it's hard to pinpoint which list items are within the visual selection, as the
  // elements themselves and all of their ancestors are considered part of the selection.
  // Nested lists are particularly problematic for this, getting the nodes in a selection
  // with nested lists will include the ancestor list items, even though they may not really
  // be selected.
  if (!editor.selection) return [];

  const [start] = Range.edges(editor.selection);

  return Array.from(
    Editor.nodes(editor, {
      match: isListItem,
      mode: Range.isCollapsed(editor.selection) ? 'lowest' : 'all',
    })
  )
    .filter(([, path]: ElementEntry) => {
      const rangeStartsAfter = Path.isAfter(start.path, path);

      if (Path.isDescendant(start.path, path)) {
        // We only want to include the listItem which contains the leaf node but skip
        // any other ancestors returned by Editor.nodes.
        const containerListItemEntry = Editor.above(editor, {
          match: isListItem,
          at: start.path,
        });
        let listItemIsContainer = false;
        if (containerListItemEntry) {
          const [, containerListItemPath] = containerListItemEntry;
          listItemIsContainer = Path.equals(path, containerListItemPath);
        }

        return !rangeStartsAfter && listItemIsContainer;
      }

      return !rangeStartsAfter;
    })
    .map(
      ([element, path]) =>
        [element, Editor.pathRef(editor, path)] as [ListItemElement, PathRef]
    );
};

export const getChildList = (
  node: Node,
  path: Path
): NodeEntry<ListElement> | undefined => {
  const children = Node.descendants(node);

  for (const candidate of children) {
    const [candidateEntry, candidatePath] = candidate;
    if (isList(candidateEntry)) {
      const fullPath = [...path, ...candidatePath];
      return [candidateEntry, fullPath];
    }
  }
};

export const getPreviousSiblingListItem = (
  editor: Editor,
  path: Path
): NodeEntry<ListItemElement> | undefined => {
  const previousSibling = NodeUtils.previousSibling(editor, path, {
    match: isListItem,
  });

  if (!previousSibling || !isListItemEntry(previousSibling)) return;

  return previousSibling;
};

// Find the list item that contains the current selection.
export const getCurrentListItems = (
  editor: Editor
): NodeEntry<ListItemElement>[] => {
  if (!editor.selection) return [];

  const currentListEntry = getParentList(editor);
  if (!currentListEntry) return [];
  const [currentList, currentListPath] = currentListEntry;
  const targetSelection = Editor.unhangRange(editor, editor.selection);

  const nodes = currentList.children
    .map((listItem, index): NodeEntry<ListItemElement> | null => {
      const listItemPath = [...currentListPath, index];
      const inSelection = Range.includes(targetSelection, listItemPath);
      if (isListItem(listItem) && inSelection) {
        return [listItem, listItemPath];
      }
      return null;
    })
    .filter((entry) => !!entry);

  return nodes;
};

// Find the list that contains the current selection.
export const getParentList = (
  editor: Editor,
  location?: Location
): NodeEntry<ListElement> | undefined => {
  const opts: EditorAboveOptions<ListElement> = {
    match: isList,
  };

  if (location) {
    opts.at = location;
  }

  return Editor.above(editor, opts);
};

// Find the list that contains the current selection, but only considering
// children that are the first child of a `list_item` - i.e. the child of
// the list item that appears connected to the dot or number. Subsequent children
// should act like they are independent blocks and not part of the list. So that
// they can then start a new list.
export const getDirectParentList = (
  editor: Editor,
  path?: Path
): NodeEntry<ListElement> | undefined => {
  // Do our own traversal so we can track the child index before the list_item.

  let pos = path || editor.selection?.anchor.path;
  while (pos) {
    const lastIndex = pos[pos.length - 1];
    const [node, nodePath] = Editor.parent(editor, pos);
    if (!node || nodePath.length === 0) return;

    if (isListItem(node) && lastIndex > 0) {
      // We are in a list, but not the first child of the list item.
      return;
    }

    if (isList(node)) {
      return [node, nodePath];
    }

    pos = nodePath;
  }
};

// Matchers

// Match function for generic list items.

export const isSelectionInList = (editor: Editor) => {
  return !!getParentList(editor);
};

export const inListOfType = (
  editor: Editor,
  listType: (typeof SUPPORTED_TYPES)[number]
): boolean => {
  const parentListEntry = getDirectParentList(editor);
  if (!parentListEntry) return false;

  const [parentList] = parentListEntry;
  return parentList.type === listType;
};

export const getNextSiblingPath = (path: Path) => [
  ...path.slice(0, -1),
  path.slice(-1)[0] + 1,
];

export const prepareListItemsForSplicing = (fragmentEditor: Editor) => {
  const newBlocks: Descendant[] = [];
  const blocks = Array.from(
    Editor.nodes<Element>(fragmentEditor, {
      at: [],
      mode: 'lowest',
      match: (n) => Element.isElement(n) && Editor.isBlock(fragmentEditor, n),
    })
  );
  const nodes = Array.from(Node.children(fragmentEditor, []));
  if (!blocks.length || !nodes.length) return fragmentEditor.children;

  // If the first node is a list, find the first block's parent list, remove
  // its children and add them at the top level. This makes sure that items
  // go into a target list at the right depth
  if (isList(nodes[0][0])) {
    const blockParentList = Editor.above(fragmentEditor, {
      at: blocks[0][1],
      match: isList,
      mode: 'lowest',
    });
    if (blockParentList) {
      const children = Node.children(fragmentEditor, blockParentList[1], {
        reverse: true,
      });
      for (const child of children) {
        newBlocks.unshift(child[0]);
        Transforms.removeNodes(fragmentEditor, { at: child[1] });
      }
      removeTree(fragmentEditor, blockParentList[1]);
    }
  }

  // Reload nodes after possibly removing the first one
  const remainingNodes = Node.children(fragmentEditor, []);
  for (const [node] of remainingNodes) {
    if (isList(node)) {
      newBlocks.push(...node.children);
    } else if (isListItem(node)) {
      newBlocks.push(node);
    } else {
      newBlocks.push({
        type: 'list_item',
        children: [node],
      });
    }
  }

  return newBlocks;
};

export const spliceFragmentIntoList = (
  editor: Editor,
  fragment: Descendant[]
) => {
  if (!editor.selection) return false;

  // Create an editor instance to work with the fragment
  const fragmentAhaEditor = new AhaEditor();
  fragmentAhaEditor.editor.children = fragment;
  const fragmentEditor = fragmentAhaEditor.editor;

  // In Slate 0.47 we could refer to nodes by key, but we can't do that
  // anymore so we need a way to track and remove the nodes we've inserted
  // from the fragmentEditor
  const insertedPaths = new Set<string>();
  const fragmentBlocks = Array.from(
    Editor.nodes<Element>(fragmentEditor, {
      at: [],
      mode: 'lowest',
      match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
    })
  );

  const startFragmentBlock = fragmentBlocks[0];
  const endFragmentBlock = fragmentBlocks[fragmentBlocks.length - 1];

  // The block that the cursor is currently in. It could be a
  // paragraph within a list item, for example
  const startBlock = Editor.above<Element>(editor, {
    at: editor.selection.anchor,
    match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
    mode: 'lowest',
  });

  // These should never be false, we're just appeasing Typescript
  if (!startBlock) return false;

  // The list item that the cursor is currently in
  const startItem = Editor.above<ListItemElement>(editor, {
    at: startBlock[1],
    match: (n) => Element.isElement(n) && n.type === 'list_item',
    mode: 'lowest',
  });

  if (!startItem) return false;

  // If there is any selected text, delete it
  if (!Range.isCollapsed(editor.selection)) {
    Transforms.delete(editor);
  }

  const atEnd = Point.equals(
    Editor.end(editor, startBlock[1]),
    editor.selection.focus
  );

  const shouldSplitBlock = !atEnd;

  Editor.withoutNormalizing(editor, () => {
    if (!editor.selection) return;

    if (shouldSplitBlock) {
      Transforms.splitNodes(editor, {
        at: editor.selection.focus,
        match: (n) => Element.isElement(n) && n.type === 'list_item',
        mode: 'lowest',
        always: true,
      });
    }

    const p = getNextSiblingPath(startBlock[1]);
    Transforms.insertNodes(editor, startFragmentBlock[0], {
      at: p,
    });

    insertedPaths.add(startFragmentBlock[1].toString());
    Transforms.mergeNodes(editor, { at: p });

    Transforms.select(editor, Editor.end(editor, startBlock[1]));

    let endBlockInserted = false;

    if (!atEnd) {
      const endItem = Editor.node(editor, getNextSiblingPath(startItem[1]));
      const endBlock = Array.from(Node.children(editor, endItem[1]))[0];

      if (startFragmentBlock !== endFragmentBlock) {
        // There's more than one block in the fragment, insert and merge the
        // end block of the fragment
        Transforms.insertNodes(editor, endFragmentBlock[0], {
          at: endBlock[1],
        });
        insertedPaths.add(endFragmentBlock[1].toString());

        Transforms.select(editor, Editor.end(editor, endBlock[1]));
        const endPath = getNextSiblingPath(endBlock[1]);
        Transforms.mergeNodes(editor, { at: endPath });
        endBlockInserted = true;
      } else {
        // There's only one block in the fragment, merge the end block back
        // into the start block
        const endItemPathRefs: PathRef[] = [];
        for (const [, path] of Node.children(editor, endItem[1])) {
          endItemPathRefs.push(Editor.pathRef(editor, path));
        }

        let targetPath = getNextSiblingPath(startBlock[1]);

        for (const pathRef of endItemPathRefs) {
          const childPath = pathRef.unref();
          if (!childPath) continue;

          Transforms.moveNodes(editor, {
            at: childPath,
            to: targetPath,
          });

          targetPath = getNextSiblingPath(targetPath);
        }
        Transforms.mergeNodes(editor, {
          at: getNextSiblingPath(startBlock[1]),
        });
        Transforms.removeNodes(editor, { at: endItem[1] });
      }
    }

    // If we've inserted all the blocks from the fragment, we're done
    if (fragmentBlocks.length === insertedPaths.size) return;

    // If the first block we inserted was part of a list, insert its siblings
    // at the same level so we maintain the nesting depth of the list
    if (getParentList(fragmentEditor, startFragmentBlock[1])) {
      const parent = Editor.parent(fragmentEditor, startFragmentBlock[1]);
      const children = Array.from(Node.children(fragmentEditor, parent[1]));
      let nextPath = startBlock[1];
      children.forEach(([childNode, childPath], index) => {
        if (childPath.toString() !== startFragmentBlock[1].toString()) {
          nextPath = getNextSiblingPath(nextPath);
          Transforms.insertNodes(editor, childNode, {
            at: nextPath,
          });
          const insertedBlocks = Array.from(
            Editor.nodes<Element>(fragmentEditor, {
              at: childPath,
              mode: 'lowest',
              match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
            })
          );
          insertedBlocks.forEach(([, path]) => {
            insertedPaths.add(path.toString());
          });
        }
      });
      if (!endBlockInserted) {
        Transforms.select(editor, Editor.end(editor, nextPath));
      }
    }

    const pathsToSort = Array.from(insertedPaths).map((p) =>
      p.split(',').map(Number)
    );
    for (const path of PathUtils.sortPathsByDepth(pathsToSort, true)) {
      removeTree(fragmentEditor, path);
    }

    const remainingBlocks = prepareListItemsForSplicing(fragmentEditor);

    if (remainingBlocks.length) {
      let nextPath = startItem[1];
      for (const block of remainingBlocks) {
        nextPath = getNextSiblingPath(nextPath);
        Transforms.insertNodes(editor, block, {
          at: nextPath,
        });
      }
      if (atEnd) {
        Transforms.select(editor, Editor.end(editor, nextPath));
      }
    }
  });

  return true;
};

// The marker should be hidden if the list item's only child is anohter list of type ul_list, ol_list, dl_list or checklist.
export function shouldHideMarker(element: ListItemElement) {
  if (element.children.length === 1) {
    const child = element.children[0];
    if (!Text.isText(child) && SUPPORTED_TYPES.includes(child.type)) {
      return true;
    }
  }
  return false;
}
